'use client';

import { getUserInfo, logOut } from './api/index';
import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useMobileLogin from '../useMobileLogin';
import { useMedia } from '../useMedia';
import { LocalGlobalContext } from '../useLocalGlobalStore';
import { useStore } from 'zustand';

export type UserInfo = {
  company: string;
  email: string;
  rawEmail?: string;
  firstName?: string;
  lastName?: string;
  position: string;
  skipStatus: number;
  hasRegistered: boolean;
};

type IntlGlobalDataContextType = {
  userInfo: UserInfo | null;
  isLogin: boolean;
  isUserInfoReady: boolean;
  login: () => void;
  logout: () => Promise<boolean>;
  refreshUserInfo: () => Promise<boolean>;
};

export const IntlGlobalDataContext = createContext<
  IntlGlobalDataContextType | undefined
>(undefined);

export const useIntlGlobalData = () => {
  const context = useContext(IntlGlobalDataContext);
  if (context === undefined) {
    throw new Error(
      'useIntlGlobalData must be used within IntlGlobalDataContextProvider'
    );
  }
  return context;
};

export const useEnvGlobalContext = () => {
  const contextIntl = useContext(IntlGlobalDataContext);
  const contextState = useContext(LocalGlobalContext);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const context = contextState && useStore(contextState);

  return { context, contextIntl };
};

interface IntlGlobalDataContextProviderProps {
  children: ReactNode;
}

const noUserInfoList = ['/login'];

export const IntlGlobalDataContextProvider: React.FC<
  IntlGlobalDataContextProviderProps
> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isUserInfoReady, setIsUserInfoReady] = useState(false);

  const loginUrl = useMobileLogin({
    coralAppId: process.env.NEXT_PUBLIC_CORAL_APPID as string,
    query: {
      s_language: 'en',
    },
  });
  const isMobile = useMedia('mobile');

  const isLogin = useMemo(() => !!userInfo, [userInfo]);

  const login = () => {
    if (isMobile) {
      window.location.href = loginUrl;
    } else {
      window.location.href = `/login?s_url=${encodeURIComponent(
        window.location.href
      )}`;
    }
  };

  const refreshUserInfo = async (): Promise<boolean> => {
    try {
      const res = await getUserInfo();
      if (res.code === 0 && res.data) {
        setUserInfo(res.data);
        return true;
      }

      setUserInfo(null);
      return false;
    } catch (error) {
      console.error('Failed to refresh intl user info', error);
      setUserInfo(null);
      return false;
    } finally {
      setIsUserInfoReady(true);
    }
  };

  const logout = async () => {
    try {
      const res = await logOut();
      if ([0, 401, 403].includes(res.code)) {
        setUserInfo(null);
        setIsUserInfoReady(true);
        return true;
      }
    } catch (error) {
      console.error('Failed to logout intl user', error);
    }
    return false;
  };

  useEffect(() => {
    const { pathname } = window.location;
    if (!noUserInfoList.includes(pathname)) {
      refreshUserInfo();
      return;
    }

    setIsUserInfoReady(true);
  }, []);

  return (
    <IntlGlobalDataContext.Provider
      value={{
        userInfo,
        isLogin,
        isUserInfoReady,
        login,
        logout,
        refreshUserInfo,
      }}
    >
      {children}
    </IntlGlobalDataContext.Provider>
  );
};
